import React, { useEffect, useState, useCallback } from 'react';
import _ from 'lodash';
import { useDispatch, useSelector, useInterval, useLogger } from '~Hooks';

import {
  State,
  TableSnapshotState,
  InventorySnapshotState,
  ReportSnapshotState,
  OrderStatusSnapshotState,
  CashSnapshotState,
  DeviceSettingsSnapshotState,
} from '~reducers';
import { TableEvent } from '@baemingo/table-events';
import { InventoryEvent } from '@baemingo/inventory-events';
import { ReportEvent } from '@baemingo/report-events';
import { OrderStatusEvent } from '@baemingo/order-status-events';
import { CashEvent } from '@baemingo/cash-events';
import moment from 'moment';
import { DeviceSettingsEvent } from '@baemingo/device-settings-events/dist/types';
import TimeManager from '~Components/TimeManager';
import { useStorage } from '~Components/Storage';

interface EventInput {
  [key: string]: any;
}

export interface PutEventsInputEvent extends EventInput {
  eventId: string;
  venueId: string;
  companyId: string;
  createdAt: string;
  sentAt: string;
  clientId: string;
}

type Event =
  | TableEvent
  | InventoryEvent
  | ReportEvent
  | OrderStatusEvent
  | DeviceSettingsEvent
  | CashEvent;

type Snapshot =
  | TableSnapshotState
  | InventorySnapshotState
  | ReportSnapshotState
  | OrderStatusSnapshotState
  | DeviceSettingsSnapshotState
  | CashSnapshotState;

interface EventsQueryResponse {
  events: Event[];
  success: boolean;
  error: null | string;
}

interface SnapshotQueryResponse {
  snapshot: any;
  timestamp: string;
  error: null | string;
}

interface Props {
  venueId: string;
  eventType: string;
  manyEventsType?: string;
  snapshotType: string;
  eventSelector: (state: State) => Event[];
  snapshotSelector: (state: State) => Snapshot;
  queryEvents: (timestamp: string) => Promise<EventsQueryResponse>;
  querySnapshot: () => Promise<SnapshotQueryResponse>;
  addEventMutation: (
    events: PutEventsInputEvent[],
  ) => Promise<{
    success: boolean;
    error: string | null;
  }>;
  sendSocketMessage?: (event: PutEventsInputEvent) => void;
  pollingInterval?: number;
}

const DEFAULT_TIMESTAMP = moment()
  .startOf('day')
  .add(4, 'hours')
  .toISOString();

/*
 * When we get a new event we need to check if we already
 * have this event locally before adding it to state.
 * This can be a VERY slow process. So we save each ID in a
 * hash map to make event lookup way faster.
 */

const eventIdHash: {
  [key: string]: boolean;
} = {};

const EventsSynchronizer: React.FunctionComponent<Props> = ({
  queryEvents,
  querySnapshot,
  eventSelector,
  snapshotSelector,
  eventType,
  manyEventsType,
  snapshotType,
  addEventMutation,
  sendSocketMessage,
  venueId,
  pollingInterval = 3000,
}) => {
  const [storage] = useStorage();

  // This is null on first mount, while we're loading
  const [eventsData, setEventsData] = useState<Event[] | null>(null);
  // Get dispatch
  const dispatch = useDispatch();
  // Get company ID
  const companyId = useSelector((state) => state.company!);

  // Get local events that have not yet been sent
  const events = useSelector(eventSelector);

  const snapshot = useSelector(snapshotSelector);

  // Store the timestamp of the last event we got from the backend to use as a filter for getting new events
  const [lastSeenEvent, setLastSeenEvent] = useState<string>(DEFAULT_TIMESTAMP);

  useInterval(() => {
    if (snapshot.loading) {
      return;
    }

    /**
     * Money saving - this polling is CRAZY expensive (due to lambda costs).
     * So cutting them by X % is huge.
     */

    // Get minutes since lastSeenEvent
    const minutesSinceLastEvent = moment().diff(moment(lastSeenEvent), 'minutes');

    const random = Math.random() * 100;

    // If it's been more than 10 minutes since an event was created, only query 50% of the time
    if (minutesSinceLastEvent > 10 && random < 50) {
      return;
    }

    // If its been more than 45 minutes since an event was created, only query 10% of the time
    if (minutesSinceLastEvent > 45 && random < 90) {
      return;
    }

    // If its been more than a day since an event was created, only query 1% of the time
    if (minutesSinceLastEvent > 1440 && random < 99) {
      return;
    }

    queryEvents(lastSeenEvent).then((response) => {
      if (!response.success) {
        console.error(response.error);
        return;
      }

      setEventsData(response.events);
    });
  }, pollingInterval);

  // Refetch table events when device wakes from sleep (without refetching the full snapshot)
  const deviceWake = useSelector((state) => state.deviceWakeState);
  const wakeLogger = useLogger('device-wake-sync');

  useEffect(() => {
    if (eventType !== 'ADD_TABLE_EVENT') {
      return;
    }

    if (deviceWake.hasSyncedTableEvents) {
      return;
    }

    wakeLogger.info('Device woke up, refetching table events', {
      lastSeenEvent,
      wakeTimestamp: deviceWake.wakeTimestamp,
    });

    queryEvents(lastSeenEvent).then((response) => {
      if (!response.success) {
        wakeLogger.error('Failed to refetch table events after wake', {
          errorMessage: response.error,
        });
        // Mark as synced even on failure so we don't get stuck in a loop
        dispatch({ type: 'DEVICE_WAKE_TABLE_SYNC_COMPLETE' });
        return;
      }

      wakeLogger.info('Successfully refetched table events after wake', {
        eventCount: response.events.length,
      });

      setEventsData(response.events);
      dispatch({ type: 'DEVICE_WAKE_TABLE_SYNC_COMPLETE' });
    });
  }, [deviceWake.hasSyncedTableEvents]);

  useEffect(() => {
    querySnapshot()
      .then((response) => {
        if (response.error) {
          console.error(response.error);

          return;
        }

        const timestamp = response.timestamp || DEFAULT_TIMESTAMP;

        setLastSeenEvent(timestamp);

        queryEvents(timestamp).then((eventsResponse) => {
          if (!eventsResponse.success) {
            console.error(eventsResponse.error);
            return;
          }

          setEventsData(eventsResponse.events);
        });

        dispatch({
          type: snapshotType,
          snapshot: response.snapshot,
        });
      })
      .catch(() => {
        const timestamp = DEFAULT_TIMESTAMP;

        setLastSeenEvent(timestamp);

        queryEvents(timestamp).then((eventsResponse) => {
          if (!eventsResponse.success) {
            console.error(eventsResponse.error);
            return;
          }

          setEventsData(eventsResponse.events);
        });

        dispatch({
          type: snapshotType,
          timestamp: TimeManager.now().toISOString(),
          snapshot: null,
        });
      });
  }, []);

  function prepareEventForSend(event: Event): PutEventsInputEvent {
    let venueIdToUse = venueId;

    if ('venueId' in event) {
      venueIdToUse = event.venueId;
    }

    return {
      clientId: storage.taxComplianceDeviceName || 'unknown',
      companyId,
      ...event,
      venueId: venueIdToUse,
      sentAt: TimeManager.now().toISOString(),
    };
  }

  /**
   * This function handles converting the events to the proper backend format and sending them off.
   * It's debounced so we dont trigger hundreds of mutations if the user does a batch action
   */
  const handleNewTableEvents = useCallback(
    _.debounce(async (eventsToSync: Event[]) => {
      // Dont run mutation if there's no events
      if (eventsToSync.length === 0) {
        return;
      }

      // Mark the events as sent BEFORE we sent the mutation so we dont accidentally send them twice
      // when someone performs an action during the mutation
      dispatch({
        type: 'MARK_EVENTS_SENT',
        ids: eventsToSync.map((event) => event.eventId),
        sentAt: TimeManager.now().toISOString(),
      });

      // Format the events into the correct type
      const eventsToSend: PutEventsInputEvent[] = eventsToSync.map(prepareEventForSend);

      try {
        const result = await addEventMutation(eventsToSend);

        if (result.success) {
          // Operation success, do nothing?
        } else {
          console.error(result.error);
          throw new Error(result.error!);
        }
      } catch (e) {
        // Un-mark the events and try again later
        dispatch({
          type: 'MARK_EVENTS_SENT',
          ids: eventsToSync.map((event) => event.eventId),
          sentAt: null,
        });
      }
    }, 750),
    [],
  );

  /**
   * This effect triggers when we get new events from backend.
   * It takes the events, dispatches them and then finally updates the "last seen" timestamp of the most
   * recent event for use in future queries.
   */
  useEffect(() => {
    if (!eventsData || eventsData.length === 0) {
      return;
    }

    let timeOfLastEvent = lastSeenEvent;

    const eventsToDispatch = eventsData.filter((e) => {
      return eventIdHash[e.eventId] !== true;
    });

    // If we dont already have this event, dispatch it
    if (manyEventsType) {
      dispatch({
        type: manyEventsType,
        events: eventsToDispatch,
      });
    } else {
      eventsToDispatch.forEach((event) => {
        dispatch({
          type: eventType,
          event,
        });
      });
    }

    eventsToDispatch.forEach((e) => {
      eventIdHash[e.eventId] = true;
    });

    timeOfLastEvent = eventsData[eventsData.length - 1].sentAt!;

    setLastSeenEvent(timeOfLastEvent);
  }, [eventsData]);

  useEffect(() => {
    if (events.length === 0) {
      return;
    }

    const newEvents = events.filter((event) => event.sentAt === null);

    if (sendSocketMessage) {
      newEvents.forEach((event) => {
        if (eventIdHash[event.eventId]) {
          return;
        }

        sendSocketMessage(prepareEventForSend(event));
      });
    }

    newEvents.forEach((e) => {
      eventIdHash[e.eventId] = true;
    });

    // Don't re-debounce handleNewTableEvents if there's no new events to send
    if (newEvents.length === 0) {
      return;
    }

    handleNewTableEvents(newEvents.slice(0, 1000));
  }, [events.length]);

  if (!eventsData) {
    return null; // <LoadingScreen />;
  } else {
    return null;
  }
};

export default EventsSynchronizer;
